import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import { scCatchServiceException } from "@/annotations";
import {
  Review,
  Page,
  ReviewOverviewSnapshot,
  ReviewReplyStatusCounts,
  GeneratedReviewResponseScript,
  GeneratedReviewResponseText,
  GeneratedReviewRequestScript,
  ReviewSourceSystem,
  ReviewType,
  ReviewReplyRequest,
  ReviewPostSummary,
} from "./dtos";
import type { ReviewArchiveReason } from "./dtos/Review";
import type { ExtraFieldsParam } from ".";

const REVIEW_SERVICE = `${Constants.API_URL}/bedrock/reviews`;

export type ReviewFilterSortOptions =
  | "effective_date"
  | "star_rating"
  | "ten_point_rating"
  | "nps_score"
  | "display_name"
  | "content"
  | "sentiment"
  | "player_name"
  | "ai_shareability_score"
  | "sentiment_shareability";
export type ReviewFilterOrderOptions = "asc" | "desc";

export interface ReviewFilterParams {
  search?: string;
  sentiment?: "positive" | "neutral" | "negative" | Array<"positive" | "neutral" | "negative">;
  sourceSystem?: ReviewSourceSystem | ReviewSourceSystem[];
  replied?: boolean;
  needingReply?: boolean;
  ratingType?: ReviewType[];
  replySkipped?: boolean;
  startDate?: string;
  endDate?: string;
  starRating?: number;
  npsScore?: number;
  tenPointRating?: number;
  npsCategory?: "promoter" | "passive" | "detractor";
  sourceSentiment?: "positive" | "negative";
  hasSourceSentiment?: boolean;
  active?: boolean;
  /** Only archived reviews; without it the list leaves them out. */
  archived?: boolean;
  playerId?: number;
  segmentationIds?: number[];
  fanId?: number;
  status?: string;
  postable?: boolean;
  approvedForSocial?: boolean;
  featured?: boolean;
  page?: number;
  perPage?: number;
  sort?: ReviewFilterSortOptions;
  order?: "asc" | "desc";
  extraFields?: ExtraFieldsParam;
}

type ReviewSnapshotEntityScope = "coach" | "players" | "segmentation";
export type ReviewSnapshotSort = "volume" | "star_rating" | "nps" | "ten_point" | "sentiment" | "replies";

export interface ReviewSnapshotParams {
  playerId?: number | null;
  segmentationDetailId?: number | null;
  withinSegmentationDetailId?: number;
  periodType?: "all_time" | "monthly" | "weekly";
  date?: string; // YYYY-MM-DD
  entityScope?: ReviewSnapshotEntityScope;
  sort?: ReviewSnapshotSort;
  order?: "asc" | "desc";
  page?: number;
  perPage?: number;
}

export interface ReviewReplyStatusCountsParams {
  playerId?: number;
  segmentationIds?: number[];
  startDate?: string;
  endDate?: string;
}

export type ReviewReplyStatus = "replied" | "skipped" | "unreplied";
export interface ReviewRequest {
  replyStatus?: ReviewReplyStatus;
  condensedQuote?: string | null;
  condensedHighlightPhrases?: string[];
  sentiment?: "positive" | "neutral" | "negative" | null;
  approvedForSocial?: boolean | null;
  aiShareabilityScore?: number | null;
}

export interface ReviewArchiveRequest {
  archiveReason: ReviewArchiveReason;
  archiveNote?: string | null;
}

export interface ReviewResponseRequest {
  userInstructions?: string;
  previousResponse?: string;
  force?: boolean;
}

export interface ReviewRequestScriptRequest {
  fanId?: number;
  transactionType?: string;
  userInstructions?: string;
  previousScript?: string;
  force?: boolean;
}

const convertSnapshotNumbers = (data: ReviewOverviewSnapshot): ReviewOverviewSnapshot => {
  const convertToNumber = (val: any) => (val === null || val === undefined ? val : Number(val));

  return {
    ...data,
    id: convertToNumber(data.id),
    coachId: convertToNumber(data.coachId),
    playerId: convertToNumber(data.playerId),
    segmentationDetailId: convertToNumber(data.segmentationDetailId),
    player: data.player
      ? {
          ...data.player,
          id: convertToNumber(data.player.id),
        }
      : data.player,
    totalReviews: convertToNumber(data.totalReviews),
    totalStarReviews: convertToNumber(data.totalStarReviews),
    avgStarRating: convertToNumber(data.avgStarRating),
    fiveStarCount: convertToNumber(data.fiveStarCount),
    fourStarCount: convertToNumber(data.fourStarCount),
    threeStarCount: convertToNumber(data.threeStarCount),
    twoStarCount: convertToNumber(data.twoStarCount),
    oneStarCount: convertToNumber(data.oneStarCount),
    totalNpsResponses: convertToNumber(data.totalNpsResponses),
    avgNpsScore: convertToNumber(data.avgNpsScore),
    npsNetScore: convertToNumber(data.npsNetScore),
    npsPromoterCount: convertToNumber(data.npsPromoterCount),
    npsPassiveCount: convertToNumber(data.npsPassiveCount),
    npsDetractorCount: convertToNumber(data.npsDetractorCount),
    totalTenPointResponses: convertToNumber(data.totalTenPointResponses),
    avgTenPointScore: convertToNumber(data.avgTenPointScore),
    totalRepliedCount: convertToNumber(data.totalRepliedCount),
    totalTextOnlyResponses: convertToNumber(data.totalTextOnlyResponses),
    sentimentPositiveCount: convertToNumber(data.sentimentPositiveCount),
    sentimentNeutralCount: convertToNumber(data.sentimentNeutralCount),
    sentimentNegativeCount: convertToNumber(data.sentimentNegativeCount),
    facebookReviewCount: convertToNumber(data.facebookReviewCount),
    zillowReviewCount: convertToNumber(data.zillowReviewCount),
    googleReviewCount: convertToNumber(data.googleReviewCount),
    socialCoachReviewCount: convertToNumber(data.socialCoachReviewCount),
  };
};

export class ReviewServices {
  @scCatchServiceException()
  static async getReviews(params?: ReviewFilterParams): Promise<Page<Review>> {
    const { data } = await axios.get<Page<Review>>(REVIEW_SERVICE, { params });
    return data;
  }

  @scCatchServiceException()
  static async getReviewSnapshots(params?: ReviewSnapshotParams): Promise<Page<ReviewOverviewSnapshot>> {
    const { date, ...rest } = params ?? {};
    const { data } = await axios.get<Page<ReviewOverviewSnapshot>>(`${REVIEW_SERVICE}/snapshots`, {
      params: {
        ...rest,
        periodDate: date,
        playerId: params?.playerId === null ? "" : params?.playerId,
        segmentationDetailId: params?.segmentationDetailId === null ? "" : params?.segmentationDetailId,
        page: params?.page || 1,
        perPage: params?.perPage || 25,
      },
    });

    return {
      ...data,
      content: data.content.map(convertSnapshotNumbers),
    };
  }

  @scCatchServiceException()
  static async getReviewSnapshot(id: number): Promise<ReviewOverviewSnapshot> {
    const { data } = await axios.get<ReviewOverviewSnapshot>(`${REVIEW_SERVICE}/snapshots/${id}`);
    return convertSnapshotNumbers(data);
  }

  @scCatchServiceException()
  static async refreshReviewSnapshot(id: number): Promise<ReviewOverviewSnapshot> {
    const { data } = await axios.post<ReviewOverviewSnapshot>(`${REVIEW_SERVICE}/snapshots/${id}/refresh`);
    return convertSnapshotNumbers(data);
  }

  @scCatchServiceException()
  static async syncPlayerReviews(
    playerId: number
  ): Promise<{ syncingNetworks: string[]; recentlySyncedNetworks: string[] }> {
    const { data } = await axios.post<{
      syncingNetworks?: string[];
      recentlySyncedNetworks?: string[];
    }>(`${REVIEW_SERVICE}/sync`, { playerId });
    return {
      syncingNetworks: Array.isArray(data.syncingNetworks) ? data.syncingNetworks : [],
      recentlySyncedNetworks: Array.isArray(data.recentlySyncedNetworks) ? data.recentlySyncedNetworks : [],
    };
  }

  @scCatchServiceException()
  static async getReplyStatusCounts(params?: ReviewReplyStatusCountsParams): Promise<ReviewReplyStatusCounts> {
    const { data } = await axios.get<ReviewReplyStatusCounts>(`${REVIEW_SERVICE}/reply_status_counts`, {
      params,
    });
    return {
      needingReply: Number(data.needingReply ?? 0),
      replied: Number(data.replied ?? 0),
      skipped: Number(data.skipped ?? 0),
    };
  }

  @scCatchServiceException()
  static async getReview(id: string | number, params?: { extraFields?: string | string[] }): Promise<Review> {
    const { data } = await axios.get<Review>(`${REVIEW_SERVICE}/${id}`, { params });
    return data;
  }

  @scCatchServiceException()
  static async getReviewPosts(id: string | number): Promise<ReviewPostSummary[]> {
    const { data } = await axios.get<ReviewPostSummary[]>(`${REVIEW_SERVICE}/${id}/posts`);
    return data;
  }

  @scCatchServiceException()
  static async generateResponseScript(
    id: string | number,
    request: ReviewResponseRequest
  ): Promise<GeneratedReviewResponseScript> {
    const { data } = await axios.post(`${REVIEW_SERVICE}/${id}/generate_script`, request);
    return data;
  }

  @scCatchServiceException()
  static async generateReviewRequestScript(request: ReviewRequestScriptRequest): Promise<GeneratedReviewRequestScript> {
    const { data } = await axios.post(`${REVIEW_SERVICE}/generate_request_script`, request);
    return data;
  }

  @scCatchServiceException()
  static async generateResponseText(
    id: string | number,
    request: ReviewResponseRequest
  ): Promise<GeneratedReviewResponseText> {
    const { data } = await axios.post(`${REVIEW_SERVICE}/${id}/generate_response`, request);
    return data;
  }

  @scCatchServiceException()
  static async featureReview(id: string | number): Promise<void> {
    await axios.post(`${REVIEW_SERVICE}/${id}/feature`);
  }

  @scCatchServiceException()
  static async updateReview(id: string | number, request: ReviewRequest): Promise<void> {
    await axios.patch(`${REVIEW_SERVICE}/${id}`, request);
  }

  /** Highlights for a quote being edited; nothing is saved to the review. */
  @scCatchServiceException(true)
  static async suggestHighlights(
    id: string | number,
    request: { condensedQuote: string }
  ): Promise<{ condensedHighlightPhrases: string[] }> {
    const { data } = await axios.post<{ condensedHighlightPhrases: string[] }>(
      `${REVIEW_SERVICE}/${id}/suggest_highlights`,
      request
    );
    return data;
  }

  @scCatchServiceException()
  static async archiveReview(id: string | number, request: ReviewArchiveRequest): Promise<Review> {
    const { data } = await axios.post<Review>(`${REVIEW_SERVICE}/${id}/archive`, request);
    return data;
  }

  @scCatchServiceException()
  static async restoreReview(id: string | number): Promise<Review> {
    const { data } = await axios.post<Review>(`${REVIEW_SERVICE}/${id}/restore`);
    return data;
  }

  @scCatchServiceException()
  static async submitReviewReply(id: string | number, request: ReviewReplyRequest): Promise<void> {
    await axios.post(`${REVIEW_SERVICE}/${id}/reply`, request);
  }
}
